<script lang="ts" setup>
import { api } from '@/api';
import { RequestSupportForDeviceRequest } from '@/api/generator';
import Dialog from '@/components/Dialog/Dialog.vue';
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import VeeSubmitButton from '@/components/forms/SubmitButton/VeeSubmitButton.vue';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import TextField from '@/components/forms/TextField/TextField.vue';
import { useVeeForm } from '@/components/forms/useVeeForm.ts';
import { success as successIcon } from '@/components/icons/icons.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore.ts';
import { mdiEmail, mdiPhone } from '@mdi/js';
import { ref } from 'vue';
import { isRegistrablePtwQaEquipment } from '@/views/qaEquipment/utils';
import { formBackendErrorHandler } from '@/errorHandling';

const modelValue = defineModel<boolean>({ type: Boolean, default: false });

const qaEquipmentStore = useQaEquipmentStore();

const emailSentSuccessfully = ref(false);

const { form } = useVeeForm<RequestSupportForDeviceRequest>({
  mode: 'edit',
  options: {
    initialValues: {
      id: qaEquipmentStore.deviceForRegistration?.id,
      supportRequestDto: {
        $type:
          'Ptw.Ptw100.AppZero.Application.Shared.DeviceComponent.Dto.SupportRequestDto, Ptw.Ptw100.AppZero.Application.Shared',
        message: undefined,
      },
    },
  },
});
const { handleSubmit, resetForm } = form;
const submit = handleSubmit(async (requestSupportForDeviceRequest) => {
  const { success } = await api.qaEquipmentCloudApi.requestSupportForDevice(
    {
      id: requestSupportForDeviceRequest.id,
      supportRequestDto: {
        ...requestSupportForDeviceRequest.supportRequestDto,
        csoErrorResponse: qaEquipmentStore.csoPortalErrorMessage,
      },
    },
    [formBackendErrorHandler(form)],
  );
  if (success) {
    emailSentSuccessfully.value = true;
    qaEquipmentStore.setCsoPortalFullErrorResponse(undefined);
  }
});

function finaliseSupportRequest() {
  modelValue.value = false;
  resetForm();
  emailSentSuccessfully.value = false;
}
</script>

<template>
  <Dialog
    v-if="
      qaEquipmentStore.deviceForRegistration &&
      isRegistrablePtwQaEquipment(qaEquipmentStore.deviceForRegistration)
    "
    v-model="modelValue"
    aria-label="RequestSupportDialog"
    hideClose
    maxWidth="480"
  >
    <template #title>{{ $t('ContactPtwCustomerSupport') }}</template>
    <template #text>
      <div class="d-flex align-center mb-4">
        <v-icon :icon="mdiPhone" class="text-secondary mr-2"></v-icon>
        <span>+49 761 49055-0</span>
      </div>
      <div class="d-flex align-center mb-8">
        <v-icon :icon="mdiEmail" class="text-secondary mr-2"></v-icon>
        <span>support@ptwdosimetry.com</span>
      </div>

      <FormLabel :label="`${$t('Name')} | ${$t('ID')}`">
        <IdentifierLabel
          :identifier="qaEquipmentStore.deviceForRegistration.iuid"
          :label="qaEquipmentStore.deviceForRegistration.name"
        />
      </FormLabel>
      <div class="d-flex flex-wrap justify-start mt-4">
        <TextField
          :label="$t('Type_one')"
          class="w-50"
          :modelValue="qaEquipmentStore.deviceForRegistration.category"
          :mode="'details'"
        />
        <TextField
          :label="$t('Model')"
          class="w-50"
          :modelValue="qaEquipmentStore.deviceForRegistration.model"
          :mode="'details'"
        />
        <TextField
          :label="$t('ArticleNumber')"
          class="w-50 gutterTop"
          :modelValue="`${qaEquipmentStore.deviceForRegistration.typeNumber}`"
          :mode="'details'"
        />
        <TextField
          :label="$t('SerialNumber')"
          class="w-50 gutterTop"
          :modelValue="qaEquipmentStore.deviceForRegistration.serialNumber"
          :mode="'details'"
        />
        <TextField
          v-if="'dpin' in qaEquipmentStore.deviceForRegistration"
          :modelValue="`${qaEquipmentStore.deviceForRegistration.dpin}`"
          :mode="'details'"
        />
      </div>

      <VeeTextarea
        :label="$t('Message')"
        :mode="emailSentSuccessfully ? 'details' : 'edit'"
        :placeholder="$t('SupportRequestMessagePlaceholder')"
        class="gutterTop"
        name="supportRequestDto.message"
        rows="8"
      />
      <v-alert
        v-if="emailSentSuccessfully"
        :icon="successIcon"
        :text="$t('MessageSentWeWillGetInTouchMsg')"
        class="text-body-1 font-weight-bold mt-8 mb-4"
        density="default"
        type="success"
        variant="tonal"
      >
      </v-alert>
    </template>
    <template #actions>
      <template v-if="emailSentSuccessfully">
        <v-spacer></v-spacer>
        <SecondaryVBtn
          :text="$t('Close')"
          class="align-self-center"
          @click="finaliseSupportRequest"
        />
        <v-spacer></v-spacer>
      </template>
      <template v-else>
        <TextVBtn @click="modelValue = false">
          {{ $t('Cancel') }}
        </TextVBtn>
        <VeeSubmitButton class="ml-4" :text="$t('Send')" @click="submit" />
      </template>
    </template>
  </Dialog>
</template>
